import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { showSuccessToast, showErrorToast } from './toastUtils';
import { rateLimiter } from './simpleRateLimiter';

export const loadFriends = async (userId) => {
    const receivedSnapshot = await getDocs(query(
        collection(firestore, "Friends"),
        where("receiver", "==", userId)
    ));

    const sentSnapshot = await getDocs(query(
        collection(firestore, "Friends"),
        where("sender", "==", userId)
    ));

    const receivedFriends = receivedSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            ...data,
            isReceived: true
        };
    });

    const sentFriends = sentSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            ...data,
            isReceived: false
        };
    });

    const friends = [...receivedFriends, ...sentFriends];
    return friends;
};

export const addFriend = async (currentUserId, currentUserEmail, searchTerm) => {
    // Check rate limit first
    if (!rateLimiter.canMakeRequest(currentUserId)) {
        throw new Error("Too many friend requests. Please wait a moment before trying again.");
    }

    // Check if friend exists by email or username
    const userSnapshot = await getDocs(query(
        collection(firestore, "users"),
        where("email", "==", searchTerm.trim())
    ));

    const usernameSnapshot = await getDocs(query(
        collection(firestore, "users"),
        where("username", "==", searchTerm.trim())
    ));

    let friendUser;
    if (!userSnapshot.empty) {
        friendUser = userSnapshot.docs[0];
    } else if (!usernameSnapshot.empty) {
        friendUser = usernameSnapshot.docs[0];
    } else {
        throw new Error("No user found with that email or username");
    }
    
    // Get current user's data to check for username and points
    const currentUserDoc = await getDoc(doc(firestore, "users", currentUserId));
    const currentUserData = currentUserDoc.data();
    
    // Check for existing friendship in both directions
    const existingSentSnapshot = await getDocs(query(
        collection(firestore, "Friends"),
        where("sender", "==", currentUserId),
        where("receiver", "==", friendUser.id)
    ));

    const existingReceivedSnapshot = await getDocs(query(
        collection(firestore, "Friends"),
        where("sender", "==", friendUser.id),
        where("receiver", "==", currentUserId)
    ));

    if (!existingSentSnapshot.empty) {
        throw new Error("Friend request already sent!");
    }

    if (!existingReceivedSnapshot.empty) {
        throw new Error("You already have a pending friend request from this user!");
    }

    // Create friend request with usernames and points
    await addDoc(collection(firestore, "Friends"), {
        sender: currentUserId,
        receiver: friendUser.id,
        senderUsername: currentUserData?.username || currentUserEmail,
        receiverUsername: friendUser.data().username || friendUser.data().email,
        senderPoints: currentUserData?.points || 0,
        receiverPoints: friendUser.data().points || 0,
        status: "pending",
        createdAt: new Date()
    });

    return true;
};

export const acceptFriend = async (friendId) => {
    const friendRef = doc(firestore, "Friends", friendId);
    await updateDoc(friendRef, { status: "accepted" });
    return friendId;
};

export const rejectFriend = async (friendId) => {
    await deleteDoc(doc(firestore, "Friends", friendId));
    return friendId;
};

export const handleAddFriend = async (currentUserId, currentUserEmail, searchTerm) => {
    if (!searchTerm?.trim()) return false;

    try {
        await addFriend(currentUserId, currentUserEmail, searchTerm);
        showSuccessToast("Friend request sent!");
        return true;
    } catch (error) {
        console.error("Error:", error);
        showErrorToast(error.message || "Failed to send friend request");
        return false;
    }
};

export const handleAcceptFriend = async (friendId, setFriendsList) => {
    try {
        await acceptFriend(friendId);
        setFriendsList(prev => prev.map(friend => 
            friend.id === friendId ? { ...friend, status: "accepted" } : friend
        ));
        showSuccessToast("Friend request accepted!");
        return true;
    } catch (error) {
        console.error("Error accepting friend:", error);
        showErrorToast("Failed to accept request");
        return false;
    }
};

export const handleRejectFriend = async (friendId, setFriendsList) => {
    try {
        await rejectFriend(friendId);
        setFriendsList(prev => prev.filter(friend => friend.id !== friendId));
        showSuccessToast("Friend request rejected");
        return true;
    } catch (error) {
        console.error("Error rejecting friend:", error);
        showErrorToast("Failed to reject request");
        return false;
    }
}; 